import React, { useEffect, useState } from 'react'
import MoreVertIcon from '@material-ui/icons/MoreVert'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import { patchAgentAction, useGetAgentActions, useGetAgentSettings, usePatchAgentAction, usePatchAgentSettings } from '../../hooks/queries/kaiAgent'
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp'
import SearchIcon from '@material-ui/icons/Search'
import { TextEditor } from './TextEditor'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'

import {
  Switch,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  Button,
  Paper,
  Container,
  CircularProgress,
  Tooltip,
} from '@material-ui/core'
import toast from 'react-hot-toast'

const KaiSettingsPage = () => {
  const [initialSettings, setInitialSettings] = useState<any>()

  const { isFetching } = useGetAgentSettings('get-kai-settings', setInitialSettings)

  return (
    <section className='bg-light py-5'>
      <Container>
        {isFetching && <CircularProgress style={{ position: 'absolute', left: '50%' }} />}
        {initialSettings && <AIAgentSettings initialSettings={initialSettings} />}
      </Container>
    </section>
  )
}

export interface AIAgentSettingsData {
  enabled: boolean
  analyzeShortage: {
    intervalMins: number
    parts: {
      id: number
      partNumber: string
      autopilot: boolean
      included: boolean
    }[]
  }
}

interface AIAgentSettingsProps {
  initialSettings: AIAgentSettingsData
}

function AIAgentSettings({ initialSettings }: AIAgentSettingsProps) {
  // Single source of truth for the whole form
  const [settings, setSettings] = useState(initialSettings)
  const [saving, setSaving] = useState(false)

  const { mutateAsync: patchAgentSettings } = usePatchAgentSettings()

  // Update local state if parent passes new settings
  useEffect(() => {
    setSettings(initialSettings)
  }, [initialSettings])

  /** Generic handler for top‑level primitives */
  const handleFieldChange =
    (path: Path, transformer = (v) => v) =>
    (e) => {
      const raw = e.target.type === 'checkbox' ? e.target.checked : e.target.value
      const value = transformer(raw)
      console.log(setByPath(settings, path, value))
      setSettings((prev) => setByPath(prev, path, value))
    }

  const saveSettings = async () => {
    setSaving(true)
    try {
      await patchAgentSettings(settings)
      toast.success('Settings saved successfully')
    } catch (err) {
      toast.error('Something went wrong. Please try again', { duration: 10000 })
      console.error('Failed to save AI Agent settings', err)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      style={{
        paddingTop: 10,
        paddingBottom: 10,
        borderRadius: 15,
        boxShadow: '0 0.125rem 0.25rem rgba(0, 0, 0, 0.075)',
        backgroundColor: 'white',
      }}
      className='rounded-lg'
    >
      <div style={{ padding: 40 }}>
        <Typography variant='h5' gutterBottom>
          Kai Agent Settings
        </Typography>
        <br />

        <Grid container spacing={1} direction='column'>
          {/* Global Enable Toggle */}
          <Grid item>
            <Grid container alignItems='center' spacing={2}>
              <Grid item xs={6} md={7}>
                <Typography>Enable Kai Agent</Typography>
              </Grid>
              <Grid item>
                <Switch checked={settings.enabled} onChange={handleFieldChange(['enabled'])} color='primary' />
              </Grid>
            </Grid>
          </Grid>

          {settings.enabled && (
            <>
              {/* Interval Setting */}
              <Grid item>
                <Grid container alignItems='center' spacing={2}>
                  <Grid item xs={6} md={7}>
                    <Typography>Part shortage check interval (minutes)</Typography>
                  </Grid>
                  <Grid item xs>
                    <TextField
                      type='number'
                      value={Math.max(60, settings.analyzeShortage.intervalMins)}
                      onChange={handleFieldChange(['analyzeShortage', 'intervalMins'], Number)}
                      fullWidth
                      InputProps={{ inputProps: { min: 60 } }}
                    />
                  </Grid>
                </Grid>
              </Grid>

              {/* Parts Table */}
              <Grid item>
                <Typography variant='h6' className='mt-4 mb-2'>
                  Parts Configuration
                </Typography>

                <Table className='w-full'>
                  <TableHead>
                    <TableRow>
                      <TableCell>Part Number</TableCell>
                      <TableCell align='center'>Agent Controlled</TableCell>
                      <TableCell align='center'>
                        Autopilot
                        <Tooltip title='If checked, agent will manage automated email communications without pre-consent from user. If un-checked, agent will ask for consent from user before each email'>
                          <InfoOutlinedIcon style={{ marginLeft: 2, paddingBottom: 2 }} fontSize='small' />
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {settings.analyzeShortage.parts.map((part, idx) => (
                      <TableRow key={part.id}>
                        <TableCell>{part.partNumber}</TableCell>
                        <TableCell align='center'>
                          <Checkbox
                            checked={part.included}
                            onChange={handleFieldChange(['analyzeShortage', 'parts', idx, 'included'])}
                            color='secondary'
                          />
                        </TableCell>
                        <TableCell align='center'>
                          <Checkbox
                            checked={part.autopilot}
                            onChange={handleFieldChange(['analyzeShortage', 'parts', idx, 'autopilot'])}
                            color='primary'
                            disabled={!part.included}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Grid>

              <Grid item style={{ alignSelf: 'center', marginTop: 20 }}>
                <Button onClick={saveSettings} variant='contained' color='primary' disabled={saving}>
                  Save
                </Button>
              </Grid>
            </>
          )}
        </Grid>
      </div>
    </div>
  )
}

type Path = Array<string | number>
/**
 * Returns a new object with a deeply‑nested value replaced immutably.
 * Only clones along the given path, leaving the rest untouched.
 */
function setByPath<T>(obj: T, path: Path, value: any): T {
  if (path.length === 0) return value as T
  const [key, ...rest] = path

  // Array case
  if (Array.isArray(obj)) {
    const idx = key as number
    return obj.map((item, i) => (i === idx ? setByPath(item, rest, value) : item)) as unknown as T
  }

  // Object case
  return {
    ...(obj as any),
    [key]: setByPath((obj as any)[key], rest, value),
  } as T
}

export default KaiSettingsPage
